import { getFirebaseUser, getStorage } from "../auth/utils";
import { io } from "socket.io-client";
import { axiosGraphQL, sendMessage } from "../api/index";
import {
  ADD_USER_TO_ROOM,
  CREATE_ROOM,
  REMOVE_USER_FROM_ROOM,
} from "../pages/Messages/MessagesService";

export class ChatUtil {
  socket = null;
  connected = false;

  // keep track of the last "messages subscription" cleanup
  currentMessagesSubscriptionCleanup = null;

  async getToken() {
    const user = await getFirebaseUser();
    if (user) return user.getIdToken(true);
  }

  async updateConnection(token) {
    this.disconnect();
    this.socket = io(process.env.REACT_APP_BASE_URL, {
      reconnectionDelayMax: 10000,
      auth: { token },
      path: "/chat/chat",
    });
    this.connected = true;
  }

  async connect() {
    if (!this.connected) {
      this.disconnect();
      const user = await getFirebaseUser();
      if (user) {
        const token = await user.getIdToken(true);
        getStorage().setItem("tokenLastValidity", Date.now().toString());
        getStorage().setItem("token", token);

        this.socket = io(process.env.REACT_APP_BASE_URL, {
          reconnectionDelayMax: 10000,
          auth: { token },
          path: "/chat/chat",
        });
        this.connected = true;
      }
    }
  }

  getRooms(callback) {
    if (!this.socket) return;
    this.socket.off("myRooms");
    this.socket.emit("getRooms");
    return this.socket.on("myRooms", callback);
  }

  getConversation({ roomId, skip = 0, limit = 20, seenBy = false }, callback) {
    if (!this.socket) return;
    this.socket.off("conversation");
    this.socket.emit("getConversation", {
      roomId,
      skip: skip * limit,
      limit,
      seenBy,
    });
    return this.socket.on("conversation", callback);
  }

  deleteMessage({ messageId }, callback) {
    if (!this.socket) return;
    this.socket.off("deleteMessage");
    this.socket.emit("deleteMessage", { messageId });
    return this.socket.on("deleteMessage", callback);
  }

  deleteRoom({ roomId }, callback) {
    if (!this.socket) return;
    this.socket.off("deleteRoom");
    this.socket.emit("deleteRoom", { roomId });
    return this.socket.on("deleteRoom", callback);
  }

  /**
   * Subscribe for "new message" events.
   * Server emits:
   *   socket.emit("subscriptions", { subscriptions: [[userId, clubId], ...] })
   * We:
   *   - filter by clubId
   *   - subscribe to the userId channel(s) for that club
   * Returns a cleanup function
   */
  subscribeForMessages = (callback, clubId = null) => {
    if (!this.socket) return () => {};

    // if we already had an active messages subscription, clean it up first
    if (this.currentMessagesSubscriptionCleanup) {
      this.currentMessagesSubscriptionCleanup();
      this.currentMessagesSubscriptionCleanup = null;
    }

    // keep track of channels (userId topics) and their handlers
    const channelHandlers = new Map();

    const subscriptionsHandler = (response) => {
      const list = Array.isArray(response?.subscriptions)
        ? response.subscriptions
        : [];

      const filtered =
        clubId == null
          ? list
          : list.filter((s) => Array.isArray(s) && s[1] === clubId);

      filtered.forEach((s) => {
        if (!s) return;
        const channel = s[0]; // userId
        if (!channel) return;

        // define a stable handler per channel
        const handler = (res) => {
          if (callback) {
            callback(res);
          }
        };

        // avoid stacked handlers for this channel
        this.socket.off(channel);
        this.socket.on(channel, handler);

        channelHandlers.set(channel, handler);
      });
    };

    // ensure we don't accumulate "subscriptions" listeners
    this.socket.off("subscriptions");
    this.socket.on("subscriptions", subscriptionsHandler);
    this.socket.emit("subscriptions");

    // cleanup function
    const cleanup = () => {
      // remove main subscriptions handler
      this.socket.off("subscriptions", subscriptionsHandler);

      // remove all userId channel handlers we registered
      channelHandlers.forEach((handler, channel) => {
        this.socket.off(channel, handler);
      });
      channelHandlers.clear();
    };

    this.currentMessagesSubscriptionCleanup = cleanup;
    return cleanup;
  };

  sendMessage = (message) => sendMessage(message);

  disconnect() {
    // also cleanup active subscription if any
    if (this.currentMessagesSubscriptionCleanup) {
      this.currentMessagesSubscriptionCleanup();
      this.currentMessagesSubscriptionCleanup = null;
    }

    if (this.socket && this.connected) {
      this.socket.disconnect();
      this.connected = false;
    }
  }

  createRoom = (name, clubId) =>
    axiosGraphQL({ query: CREATE_ROOM, variables: { name, club: clubId } })
      .then((r) => r?.data?.data?.createRoom)
      .catch((e) => {
        throw e;
      });

  addUserToRoom = (userId, roomId, isPrivate = false) =>
    axiosGraphQL({
      query: ADD_USER_TO_ROOM,
      variables: { userId, roomId, isPrivate },
    })
      .then((r) => (r?.data?.errors ? r?.data : r?.data?.data?.addUserToRoom))
      .catch((e) => {
        throw e;
      });

  removeUserFromRoom = (userId, roomId) =>
    axiosGraphQL({
      query: REMOVE_USER_FROM_ROOM,
      variables: { userId, roomId },
    })
      .then((r) => r?.data?.data?.removeUserFromRoom)
      .catch((e) => {
        throw e;
      });
}

export const chatUtil = new ChatUtil();
